import React, { Component, ReactNode } from 'react'
import { getIcon } from '../icons/index.js'
import { t } from '../../i18n/runtime.js'

const RefreshIcon = getIcon('refresh')

interface Props {
  children: ReactNode
  fallback?: ReactNode
  portletTitle?: string
  portletConfig?: any
  cubeQuery?: string
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: string | null
}

export default class ChartErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Update state with error details
    this.setState({
      error,
      errorInfo: errorInfo.componentStack || null
    })

    // Log the error for debugging
    console.error('Chart Error Boundary caught a rendering error:', error, errorInfo)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error display
      return (
        <div className="dc:flex dc:flex-col dc:items-center dc:justify-center dc:w-full dc:h-full dc:p-6 dc:text-center dc:border dc:border-dashed dc:rounded-lg"
          style={{ borderColor: 'var(--dc-border)', backgroundColor: 'var(--dc-surface)' }}>
          <div className="dc:h-12 dc:w-12 dc:mb-4 text-dc-text-muted">⚠️</div>
          <h3 className="dc:text-lg dc:font-semibold dc:mb-2 text-dc-text">
            {this.props.portletTitle ? `Unable to render ${this.props.portletTitle}` : 'Unable to render chart'}
          </h3>
          <p className="dc:text-sm text-dc-text-secondary dc:mb-4 dc:max-w-md">
            {t('error.renderDescription')}
          </p>

          {/* Error details */}
          <div className="dc:w-full dc:max-w-2xl dc:mb-4">
            <div className="bg-dc-surface-secondary dc:rounded-lg dc:p-3 dc:text-left">
              <div className="dc:text-xs dc:font-mono dc:mb-2 text-dc-text">
                <strong>{t('error.errorLabel')}</strong> {this.state.error?.message}
              </div>
              {this.state.error?.name && (
                <div className="dc:text-xs dc:font-mono text-dc-text-secondary dc:mb-2">
                  <strong>{t('error.typeLabel')}</strong> {this.state.error.name}
                </div>
              )}

              {/* Portlet Config Debug Info */}
              {this.props.portletConfig && (
                <details className="dc:text-xs dc:font-mono text-dc-text-secondary dc:mb-2">
                  <summary className="dc:cursor-pointer">{t('error.portletConfig')}</summary>
                  <pre className="dc:mt-2 dc:whitespace-pre-wrap dc:p-2 dc:rounded-sm dc:overflow-auto dc:max-h-32"
                    style={{ backgroundColor: 'rgba(var(--dc-primary-rgb), 0.1)' }}>
                    {JSON.stringify(this.props.portletConfig, null, 2)}
                  </pre>
                </details>
              )}

              {/* Cube Query Debug Info */}
              {this.props.cubeQuery && (
                <details className="dc:text-xs dc:font-mono text-dc-text-secondary dc:mb-2">
                  <summary className="dc:cursor-pointer">{t('error.cubeQuery')}</summary>
                  <pre className="dc:mt-2 dc:whitespace-pre-wrap dc:p-2 dc:rounded-sm dc:overflow-auto dc:max-h-32"
                    style={{ backgroundColor: '#d1fae5' }}>
                    {typeof this.props.cubeQuery === 'string' 
                      ? JSON.stringify(JSON.parse(this.props.cubeQuery), null, 2)
                      : JSON.stringify(this.props.cubeQuery, null, 2)
                    }
                  </pre>
                </details>
              )}

              {this.state.errorInfo && (
                <details className="dc:text-xs dc:font-mono text-dc-text-secondary">
                  <summary className="dc:cursor-pointer">{t('error.componentStack')}</summary>
                  <pre className="dc:mt-2 dc:whitespace-pre-wrap">{this.state.errorInfo}</pre>
                </details>
              )}
            </div>
          </div>

          {/* Reset button */}
          <button
            onClick={this.handleReset}
            className="dc:px-3 dc:py-1 text-white dc:rounded-sm dc:text-sm dc:hover:opacity-90 dc:transition-opacity"
            style={{
              backgroundColor: 'var(--dc-primary)'
            }}
          >
            <RefreshIcon style={{ width: '16px', height: '16px', display: 'inline', marginRight: '4px' }} />{t('error.tryAgain')}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
